import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Link, useRouter } from '~/utils/navigation';
import styled, { css } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, ButtonPrimary, Caption, LabelRegular } from '@comeon/ui';

import { authenticationSelector } from '~/components/authentication/ducks/selectors.js';
import { selectBalance, selectCurrency, selectLocale } from '~/components/account/ducks/index.js';
import Icon, { ICON_BOTTOM, ICON_OPTIONS_VERTICAL, ICON_RIGHT, ICON_TRASH_LINE } from '~/components/icon/index.js';
import { Collapse, CollapseGroup } from './collapse.js';
import currencyFormatter from '../../../../libs/currency-formatter.js';
import useTranslations from '../../../translation/use-translations';
import {
	FETCH_SESSIONS,
	fetchPrivateSessions,
	fetchProfile,
	fetchSessions,
	playerBalanceInEventCurrency,
	setPlayerBalanceInEventCurrency,
	setTemporaryBuyIn
} from '../ducks/we-spin-duck.js';
import {
	selectConfiguration,
	selectDisplayName,
	selectHasMadeDeposit,
	selectIsSessionOnline,
	selectPlayerBalanceInEventCurrency,
	selectTemporaryBuyIn,
	selectWeSpinSuggestedBuyInEnabled
} from '../ducks/we-spin-selectors.js';
import abbrNum from '../utils/abbreviate-number.js';

import {
	WE_SPIN_BUY_IN_HIGH_AMOUNT,
	WE_SPIN_BUY_IN_LOW_AMOUNT,
	WE_SPIN_BUY_IN_MID_AMOUNT,
	WE_SPIN_BUY_IN_TEXT_KEYS,
	WE_SPIN_PREVIEW_ROOM_TEXT_KEYS
} from '../constants/we-spin-text-keys.js';
import WeSpinPillsLikeButton from './we-spin-pills-like-button.js';
import { FREE_WESPIN, LABEL_MAPPING } from '../constants/we-spin-boost-constants.js';
import useContextualPathTransformer from '../../../../hooks/use-contextual-path-transformer.js';
import {
	ROUTE_ACCOUNT_WE_SPIN_DASHBOARD,
	ROUTE_ACCOUNT_WE_SPIN_DASHBOARD_EDIT_SESSION,
	ROUTE_ACCOUNT_WE_SPIN_DASHBOARD_SETTINGS,
	ROUTE_WE_SPIN_GAME_ROOM_BUY_IN,
	ROUTE_WE_SPIN_GAME_ROOM_LIST_PLAYERS,
	ROUTE_WE_SPIN_GAME_ROOM_TOC,
	ROUTE_WE_SPIN_GAME_VIEW,
	ROUTE_WE_SPIN_LOBBY
} from '../constants/we-spin-route-constants.js';
import { selectEventByRef, selectIsLive, selectIsStreamer, selectProfile } from '../ducks/we-spin-dashboard-duck.js';
import { selectLicense } from '../../../brand/ducks/selectors.js';
import { LICENSE_SGA } from '~/utils/license-utils.js';
import useLoginFlow from '~/hooks/use-login-flow.js';
import { Input, InputBorder, InputHint } from './we-spin-game-room-buy-in-base';
import Loading from '~/components/misc/loading';
import {
	eventTagDescriptionText,
	eventTagTitleText,
	WE_SPIN_EVENT_TAG_ALL_TEXT_KEYS
} from '../constants/we-spin-event-tags';
import {
	WE_SPIN_DASHBOARD_EVENT_DETAIL_VIEW_TEXT_KEYS,
	WE_SPIN_DASHBOARD_SCHEDULED_TEXT_KEYS
} from '../constants/we-spin-dashboard-text-keys';
import WeSpinTable from '../components/we-spin-table';
import { ANNOUNCED, LIVE_STATES, PAST_STATES, PENDING_START, RUNNING } from '../constants/we-spin-stream-states';
import RemoveEventModal from '../components/modals/remove-event-modal';
import UpdateUsernameModal from '../components/modals/update-username-modal';
import JoinFreespinModal from '../components/modals/join-freespin-modal';
import BuyinModal from '../components/modals/buyin-modal';
import BuyinRefundModal from '../components/modals/buyin-refund-modal';
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader } from '~/components/sidebar';
import { fetchCurrentEvent, selectCurrentEvent, setCurrentEventRef } from '../ducks/we-spin-home-game-duck';
import StreamElement from './stream-element';
import { getWeekDayDateAndMonth } from '../utils/we-spin-time-helper';
import generateSrcSet from '~/utils/generateSrcSet';
import SidebarScrollAware from '~/utils/sidebar-scroll-aware';
import { LeftSideIconType } from '~/components/sidebar/sidebar-header/sidebar-header';
import BuyInAmount from '../components/buy-in-amount';
import useIntervalAction from '~/hooks/use-interval-action';
import AdjustBuyinModal from '../components/modals/adjust-buyin-modal';
import UnregisterFreespinModal from '../components/modals/unregister-freespin-modal';
import theme from 'styled-theming';
import { ROUTE_WE_SPIN } from '~/components/router/constants';

const TEXT_KEYS = [
	...WE_SPIN_BUY_IN_TEXT_KEYS,
	...WE_SPIN_EVENT_TAG_ALL_TEXT_KEYS,
	...WE_SPIN_DASHBOARD_EVENT_DETAIL_VIEW_TEXT_KEYS,
	...WE_SPIN_DASHBOARD_SCHEDULED_TEXT_KEYS,
	...WE_SPIN_PREVIEW_ROOM_TEXT_KEYS
];

const SIDEBAR_TITLE_SCROLL_THRESHOLD = 214;

const InputWrapper = styled(Input)`
	&:-webkit-autofill:focus,
	&:focus {
		color: rgb(var(--input-font-color));
		${theme('brand', {
			comeon: css`
				color: rgba(var(--primary-accent-green), 1) !important;
			`,
			suomikasino: css`
				color: rgba(var(--font-dark), 1) !important;
			`,
			getlucky: css`
				color: rgba(var(--font-dark), 1) !important;
			`,
			mobilebet: css`
				color: rgba(var(--font-light), 1) !important;
			`,
			nopeampi: css`
				color: rgba(var(--font-dark), 1) !important;
			`,
			galaksino: css`
				color: rgba(var(--font-light), 1) !important;
			`
		})}
	}
`;

const HeaderWrapper = styled(SidebarHeader)`
	svg {
		fill: rgb(var(--base-01)) !important;
	}
`;

const StyledSettingsLink = styled(LabelRegular)`
	color: var(--we-spin-dashboard-settings-link, rgba(var(--primary), 1));
	background-color: rgba(var(--primary-accent-grey), 1);
	padding: 4px;
	border-radius: 50%;
`;

const BoxWithBackgroundColor = styled(Box)`
	background-color: var(--button-wrapper-bg);
`;

const BoxWithDisabledBackgroundColor = styled(Box)`
	background-color: var(--link-list, rgba(var(--base-10), 1));
`;

const RefundButton = styled(Button)`
	background-color: var(--sidebar-bg, rgba(var(--base-11), 1));
	border-radius: var(--button-border-radius, 3px);
`;

const StreamElementWrapper = styled.div`
	margin-top: -45px;

	h1,
	p {
		padding-left: 12px;
	}
`;

const BUY_IN_AMOUNT_TEXT_KEYS = [WE_SPIN_BUY_IN_LOW_AMOUNT, WE_SPIN_BUY_IN_MID_AMOUNT, WE_SPIN_BUY_IN_HIGH_AMOUNT];

const inputRegex = new RegExp(/[^\d,.]/g, 'g');

const WeSpinGameRoomBuyIn = () => {
	const params = useParams();
	const location = useLocation();
	const router = useRouter();
	const dispatch = useDispatch();
	const transformPath = useContextualPathTransformer();

	const { translations, isLoadingTranslations } = useTranslations(TEXT_KEYS);

	// Common Data
	const streamView = useSelector(selectCurrentEvent);

	useEffect(() => {
		if (streamView?.state === RUNNING) {
			router.replace(`${ROUTE_WE_SPIN_GAME_VIEW}/${params.streamRef}`);
		}
	}, [router, params.streamRef, streamView]);

	const isSessionOnline = useSelector(selectIsSessionOnline(params.streamRef));
	const authenticated = useSelector(authenticationSelector);
	const { weSpinViewListPollInterval, hasWeSpinFriends } = useSelector(selectConfiguration);
	const displayName = useSelector(selectDisplayName);
	const walletBalanceInPlayersCurrency = useSelector(selectBalance) || 0;
	const buyInAmount = useSelector(selectTemporaryBuyIn(params.streamRef || ''));
	const [lowestBuyInAmount] = useState(0);
	const license = useSelector(selectLicense);
	const isStreamer = useSelector(selectIsStreamer);
	const ownsThisEvent = !!useSelector(selectEventByRef(params.streamRef));
	const isBuyInDisallowed = license === LICENSE_SGA && isStreamer && !streamView?.isPrivateStreamer && ownsThisEvent;
	const buyInDisabled =
		(authenticated && (walletBalanceInPlayersCurrency === null || walletBalanceInPlayersCurrency <= 0)) ||
		streamView?.balanceCapMet ||
		buyInAmount <= 0 ||
		// eslint-disable-next-line no-unsafe-optional-chaining
		buyInAmount > streamView?.balanceCap - streamView?.balance ||
		buyInAmount > streamView?.balanceCap ||
		buyInAmount > walletBalanceInPlayersCurrency ||
		Number(buyInAmount) < lowestBuyInAmount ||
		isBuyInDisallowed;
	const playerCurrency = useSelector(selectCurrency);
	const locale = useSelector(selectLocale);
	const buyInBalance = useMemo(
		() => (buyInAmount > 0 ? currencyFormatter.format(buyInAmount || 0, playerCurrency, locale) : ''),
		[buyInAmount, playerCurrency, locale]
	);
	const hasMadeDeposit = useSelector(selectHasMadeDeposit);
	const walletBalance = useSelector(selectPlayerBalanceInEventCurrency(params.streamRef)) || 0;
	const walletBalanceFormatted = useMemo(
		() => (walletBalance !== null ? currencyFormatter.format(walletBalance, playerCurrency, locale) : ''),
		[walletBalance, playerCurrency, locale]
	);
	const isStreamerLive = useSelector(selectIsLive);
	const profile = useSelector(selectProfile);
	const { hasBetSizeRange, hasMultipleGamesSelection } = useSelector(selectConfiguration);
	const isFreeWeSpin = streamView?.boost ? streamView.boost.type === FREE_WESPIN : false;
	const showBuyInForm =
		!isFreeWeSpin &&
		!streamView?.joined &&
		!PAST_STATES.includes(streamView?.state) &&
		!LIVE_STATES.includes(streamView?.state);
	const suggestedBuyInEnabled = useSelector(selectWeSpinSuggestedBuyInEnabled);
	const inputPlaceholder = !suggestedBuyInEnabled
		? translations.WE_SPIN_BUY_IN_AMOUNT_PLACEHOLDER_CUSTOM
		: translations.WE_SPIN_BUY_IN_AMOUNT_PLACEHOLDER;
	const [submitting] = useState(false);
	const bonusName = useMemo(
		() =>
			eventTagTitleText(streamView?.eventTags[0], translations)
				?.toLowerCase()
				.split(' ')
				.map(item => item.charAt(0).toUpperCase() + item.slice(1))
				.join(' '),
		[streamView?.eventTags, translations]
	);

	const headerIcon = useMemo(() => {
		if (location?.state?.returnLink) {
			return location?.state?.returnLink !== ROUTE_WE_SPIN_LOBBY
				? LeftSideIconType.BACK_ICON_BG
				: LeftSideIconType.CLOSE_ICON_BG;
		}
		if (ownsThisEvent) {
			return LeftSideIconType.BACK_ICON_BG;
		}
		return LeftSideIconType.CLOSE_ICON_BG;
	}, [location?.state?.returnLink, ownsThisEvent]);

	// Modals control
	const [confirmRemove, setConfirmRemove] = useState(false);
	const [usernameQuestion, setUsernameQuestion] = useState(false);
	const [showFreeWeSpinQuestion, setShowFreeWeSpinQuestion] = useState(false);
	const [adjustAmountQuestion, setAdjustAmountQuestion] = useState(false);
	const [buyInQuestion, setBuyInQuestion] = useState(false);
	const [showRefundConfirmation, setShowRefundConfirmation] = useState(false);
	const [showUnregisterFreeWeSpin, setShowUnregisterFreeWeSpin] = useState(false);

	// Modals Data
	const hasBuyInAmountChecks = !!streamView?.isPrivateStreamer;
	// eslint-disable-next-line no-unsafe-optional-chaining
	const [cappedAmount] = useState(streamView?.balanceCap - streamView?.balance);
	const startDate = new Date(streamView?.start);
	// eslint-disable-next-line no-unsafe-optional-chaining
	const endTime = new Date(startDate.getTime() + streamView?.durationInMinutes * 60000);

	// StreamElement Data
	const topText = streamView?.name || streamView?.games?.[0]?.gameName;
	const headerText = getWeekDayDateAndMonth(streamView?.start, translations);
	const hasEventTag = streamView?.eventTags?.length > 0;
	const descriptionText = hasEventTag
		? eventTagDescriptionText(streamView?.eventTags[0], translations)
		: streamView?.description;
	const currentGame = streamView?.currentGame?.gameCode ? streamView?.currentGame : null;
	const gameImageUrl = currentGame?.gameImageUrl;
	const backgroundImage = streamView?.streamBgUrl || streamView?.streamerImgUrl || gameImageUrl || null;
	const backgroundImageSrcSet = backgroundImage ? generateSrcSet(backgroundImage, true) : null;

	// Actions
	const { initializeLogin } = useLoginFlow();
	const onLeftSideButtonClick = useCallback(() => {
		if (location?.state?.returnLink) {
			router.push(transformPath(location?.state?.returnLink));
		} else if (ownsThisEvent) {
			router.push(transformPath(ROUTE_ACCOUNT_WE_SPIN_DASHBOARD));
		} else {
			router.push(ROUTE_WE_SPIN);
		}
	}, [location?.state?.returnLink, router, ownsThisEvent, transformPath]);
	const showBuyInQuestion = useCallback(() => {
		if (hasBuyInAmountChecks && cappedAmount < buyInAmount) {
			setAdjustAmountQuestion(true);
		} else {
			setBuyInQuestion(true);
		}
	}, [setAdjustAmountQuestion, cappedAmount, buyInAmount, setBuyInQuestion, hasBuyInAmountChecks]);
	const showUsernameQuestionOrBuyInQuestion = useCallback(() => {
		if (!authenticated) {
			initializeLogin({
				from: `${location.pathname}${location.search}`
			});
		} else if (!displayName) {
			setUsernameQuestion(true);
		} else {
			showBuyInQuestion();
		}
	}, [authenticated, displayName, initializeLogin, location.pathname, location.search, showBuyInQuestion]);
	const showUsernameQuestionOrJoinFreeWeSpin = useCallback(() => {
		if (!authenticated) {
			initializeLogin({
				from: `${location.pathname}${location.search}`
			});
		}
		if (!displayName) {
			setUsernameQuestion(true);
		} else {
			setShowFreeWeSpinQuestion(true);
		}
	}, [authenticated, displayName, initializeLogin, location.pathname, location.search]);
	const proceedToJoinFreeWeSpin = () => {
		setUsernameQuestion(false);
		setShowFreeWeSpinQuestion(true);
	};
	const proceedWithBuyIn = useCallback(() => {
		setUsernameQuestion(false);
		showBuyInQuestion();
	}, [setUsernameQuestion, showBuyInQuestion]);
	const setBuyInAmount = useCallback(
		amount => {
			dispatch(setTemporaryBuyIn(amount, params.streamRef));
		},
		[dispatch, params.streamRef]
	);
	const updateBuyInAmount = useCallback(
		amount => {
			setBuyInAmount(amount);
		},
		[setBuyInAmount]
	);

	// Init Data
	useEffect(() => {
		dispatch(fetchProfile());
		dispatch(setCurrentEventRef(params.streamRef));
		dispatch(fetchCurrentEvent());
	}, [dispatch, params.streamRef]);
	useEffect(() => {
		if (params.streamRef && authenticated) {
			dispatch(playerBalanceInEventCurrency(params.streamRef));
		} else {
			setPlayerBalanceInEventCurrency(null, params.streamRef);
		}
		return () => {
			dispatch(setPlayerBalanceInEventCurrency(null, params.streamRef));
		};
	}, [dispatch, walletBalanceInPlayersCurrency, params.streamRef, authenticated]);

	// Pool Data
	useIntervalAction(
		FETCH_SESSIONS,
		() => {
			dispatch(fetchSessions());
			if (hasWeSpinFriends) {
				dispatch(fetchPrivateSessions());
			}
		},
		weSpinViewListPollInterval || 5000
	);

	if (isLoadingTranslations || !streamView || !location.pathname.includes(streamView.streamRef)) return <Loading />;

	return (
		<Sidebar className="wespin-sidebar">
			<SidebarScrollAware>
				{scrollYPosition => (
					<HeaderWrapper
						transparent={scrollYPosition < SIDEBAR_TITLE_SCROLL_THRESHOLD}
						className="transparent_title"
						opaqueClassName="page-head--opac"
						leftSideIcon={headerIcon}
						onLeftSideButtonClick={onLeftSideButtonClick}
					>
						<div className="page-head__right-side">
							{location.pathname !== ROUTE_ACCOUNT_WE_SPIN_DASHBOARD_SETTINGS && !isStreamerLive && (
								<div>
									{!!profile.screenName && !!profile.description && (
										<Link href={transformPath(ROUTE_ACCOUNT_WE_SPIN_DASHBOARD_SETTINGS)}>
											<StyledSettingsLink my={1}>
												<Icon type={ICON_OPTIONS_VERTICAL} />
											</StyledSettingsLink>
										</Link>
									)}
								</div>
							)}
						</div>
					</HeaderWrapper>
				)}
			</SidebarScrollAware>
			<SidebarContent>
				{/* Modals */}
				<RemoveEventModal
					openConfirmRemove={confirmRemove}
					closeConfirmRemove={() => {
						setConfirmRemove(false);
					}}
					streamRef={params.streamRef}
				/>
				<UpdateUsernameModal
					openUpdateUsername={usernameQuestion}
					closeUpdateUsername={() => {
						setUsernameQuestion(false);
					}}
					onSuccess={isFreeWeSpin ? proceedToJoinFreeWeSpin : proceedWithBuyIn}
				/>
				<JoinFreespinModal
					openJoinFreespin={showFreeWeSpinQuestion}
					closeJoinFreespin={() => {
						setShowFreeWeSpinQuestion(false);
					}}
					streamRef={params.streamRef}
					startDate={startDate}
					endTime={endTime}
				/>
				<UnregisterFreespinModal
					isOpen={showUnregisterFreeWeSpin}
					close={() => {
						setShowUnregisterFreeWeSpin(false);
					}}
					streamRef={streamView.streamRef}
				/>
				<BuyinModal
					openBuyin={buyInQuestion}
					closeBuyin={() => {
						setBuyInQuestion(false);
					}}
					streamRef={params.streamRef}
					currentGame={streamView.currentGame}
					streamerName={streamView.streamerName}
				/>
				<BuyinRefundModal
					openBuyinRefund={showRefundConfirmation}
					closeBuyinRefund={() => {
						setShowRefundConfirmation(false);
					}}
					streamRef={params.streamRef}
					isFreeWespin={isFreeWeSpin}
				/>
				<AdjustBuyinModal
					openAdjustBuyin={adjustAmountQuestion}
					closeAdjustBuyin={() => {
						setAdjustAmountQuestion(false);
					}}
					streamRef={params.streamRef}
				/>
				{/* <BalanceCapMetModal balanceCapMet={streamView.balanceCapMet} closeBalanceCapMet={onCloseSidebar} /> */}
				{/* /Modals */}

				<StreamElementWrapper className="top-of-page-image-wrapper u-relative">
					<StreamElement
						showBoost
						size="small"
						type={isSessionOnline ? 'STREAM' : 'IMAGE'}
						streamLinkUrl={isSessionOnline ? streamView.streamUrl : backgroundImage}
						topText={topText}
						headerText={headerText}
						streamerDescription={descriptionText}
						boost={streamView.boost}
						srcSet={backgroundImageSrcSet}
						streamView={streamView}
						showEventTagBg={!streamView.streamBgUrl}
					/>
				</StreamElementWrapper>
				<div className="u-pb2">
					{streamView.joined && !isFreeWeSpin && (
						<div className="u-m1 u-mb2">
							<BuyInAmount amount={streamView.buyInAmount} />
						</div>
					)}

					<div className="bottom-half wespin-buyin-form">
						{showBuyInForm && (
							<>
								<div className="form">
									<fieldset className="u-relative u-oh u-p1">
										<InputWrapper
											className="input-amount u-text-c we-spin-buy-in-input"
											disabled={isBuyInDisallowed}
											type="text"
											id="new-stream-game"
											min="0"
											placeholder={isBuyInDisallowed ? '' : inputPlaceholder}
											onChange={e => {
												updateBuyInAmount(
													parseInt(
														e.target.value
															.replace(
																currencyFormatter
																	.format(0, playerCurrency, locale, true)
																	.replace('0', '')
																	.replace(' ', ''),
																''
															)
															.replace(inputRegex, ''),
														10
													)
												);
											}}
											value={
												currencyFormatter.format(
													buyInAmount || 0,
													playerCurrency,
													locale,
													true
												) ||
												buyInAmount ||
												''
											}
											autocomplete="off"
											required
										/>
										<label htmlFor="new-stream-game">{inputPlaceholder}</label>
										<InputBorder
											className="form__border"
											error={
												buyInAmount !== '' &&
												(buyInAmount > streamView.balanceCap - streamView.balance ||
													buyInAmount === 0 ||
													buyInAmount < lowestBuyInAmount ||
													buyInAmount > walletBalanceInPlayersCurrency)
											}
										/>

										{buyInAmount !== '' && buyInAmount < lowestBuyInAmount && (
											<InputHint className="hint" buyInAmount={buyInAmount}>
												{`${currencyFormatter.format(
													lowestBuyInAmount,
													playerCurrency,
													locale
												)} ${translations.WE_SPIN_BUY_IN_MINIMUM_AMOUNT}`}
											</InputHint>
										)}
										{buyInAmount > streamView.balanceCap - streamView.balance &&
											(streamView.balance * 100) / streamView.balanceCap < 90 && (
												<InputHint
													className="hint"
													error={buyInAmount > streamView.balanceCap - streamView.balance}
												>
													{`${
														translations.WE_SPIN_BUY_IN_MAX_AMOUNT_REACHED
													} ${currencyFormatter.format(
														streamView.balanceCap - streamView.balance,
														playerCurrency,
														locale
													)}`}
												</InputHint>
											)}
										{(streamView.balance * 100) / streamView.balanceCap >= 90 && (
											<InputHint
												className="hint"
												error={buyInAmount > streamView.balanceCap - streamView.balance}
											>
												{`${
													translations.WE_SPIN_BUY_IN_MAX_AMOUNT_ALMOST_REACHED
												} ${currencyFormatter.format(
													streamView.balanceCap - streamView.balance,
													playerCurrency,
													locale
												)}`}
											</InputHint>
										)}
										{buyInAmount > walletBalanceInPlayersCurrency && (
											<InputHint className="hint" error>
												{`${translations.WE_SPIN_BUY_IN_INSUFFICIENT_FUNDS}`}
											</InputHint>
										)}
									</fieldset>
								</div>
								{suggestedBuyInEnabled && streamView.buyinAmounts && (
									<div className="u-flex u-pr1 u-pl1 buy-in-buttons">
										{streamView.buyinAmounts.map((amount, i) => (
											<div key={amount} className="u-text-c u-flex-grow-1">
												<Caption>{translations[BUY_IN_AMOUNT_TEXT_KEYS[i]]}</Caption>
												<WeSpinPillsLikeButton
													onClick={() => {
														updateBuyInAmount(amount);
													}}
													disabled={
														!(
															!isBuyInDisallowed &&
															!submitting &&
															streamView.balanceCap >= streamView.balance + amount &&
															(!authenticated ||
																(!!walletBalance && walletBalance >= amount))
														)
													}
													active={Number.parseInt(buyInAmount, 10) === amount}
												>
													{currencyFormatter.format(amount, playerCurrency, locale, true)}
												</WeSpinPillsLikeButton>
											</div>
										))}
									</div>
								)}
							</>
						)}
					</div>

					{(hasEventTag || streamView.boost || hasBetSizeRange) && (
						<div className="u-pl1 u-pr1">
							<WeSpinTable className="transactions-details-table u-mt2">
								<tbody>
									<tr>
										<td>
											<span>Event Type</span>
										</td>
										<td className="u-text-r">
											<span>{bonusName || 'Regular'}</span>
										</td>
									</tr>
									{streamView.boost && (
										<>
											<tr>
												<td>
													<span>Boost Type</span>
												</td>
												<td className="u-text-r">
													<span>{LABEL_MAPPING[streamView.boost.type]}</span>
												</td>
											</tr>
											<tr>
												<td>
													<span>Boost Amount</span>
												</td>
												<td className="u-text-r">
													<span>
														{currencyFormatter.format(
															streamView.boost.amount,
															streamView.boost.currency,
															locale
														)}
													</span>
												</td>
											</tr>
										</>
									)}
									{hasBetSizeRange && (
										<tr>
											<td>
												<span>{translations.WE_SPIN_BET_SIZE_RANGE_LABEL}</span>
											</td>
											<td className="u-text-r">
												<span>
													{[
														abbrNum(streamView.betSizeRangeMin),
														abbrNum(streamView.betSizeRangeMax)
													]
														.map(v => (v ? `${v} ${streamView.currencyCode}` : '###'))
														.join(' - ')}
												</span>
											</td>
										</tr>
									)}
								</tbody>
							</WeSpinTable>
						</div>
					)}
					<div className="u-p1">
						{authenticated && playerCurrency !== streamView.currencyCode && (
							<Collapse
								label={`${translations.WE_SPIN_BUY_IN_MY_BALANCE_IN_LABEL} ${playerCurrency}`}
								text={translations.WE_SPIN_BUY_IN_MY_BALANCE_IN_TEXT}
								dataAt="collapse-button-games-excluded-bonus"
								value={walletBalanceFormatted}
								className="we-spin-buyin__collapse"
								icon={<Icon className="collapse__continue-icon" type={ICON_BOTTOM} />}
							/>
						)}
						{ownsThisEvent && (
							<CollapseGroup className="u-mb1">
								<Collapse
									type="link"
									label={translations.WE_SPIN_BUY_IN_PLAYERS_LABEL}
									dataAt="collapse-button-games-excluded-bonus"
									link={transformPath(`${ROUTE_WE_SPIN_GAME_ROOM_LIST_PLAYERS}/${params.streamRef}`)}
									value={streamView.noParticipants}
									className="we-spin-buyin__collapse"
									icon={<Icon className="collapse__continue-icon" type={ICON_RIGHT} />}
								/>

								{/* <Collapse */}
								{/*	type="link" */}
								{/*	label={translations.WE_SPIN_PREVIEW_ROOM_RAFFLE_LABEL} */}
								{/*	dataAt="collapse-button-games-excluded-bonus" */}
								{/*	link={`${transformPath(ROUTE_WE_SPIN_GAME_ROOM_LIST_RAFFLE)}/${params.streamRef}`} */}
								{/*	value={myRaffleTicketCount || 0} */}
								{/*	className="we-spin-buyin__collapse" */}
								{/*	icon={<Icon className="collapse__continue-icon" type={ICON_RIGHT} />} */}
								{/* /> */}
							</CollapseGroup>
						)}

						{hasMultipleGamesSelection && streamView.games && (
							<CollapseGroup>
								<Collapse
									label={`${translations.WE_SPIN_BUY_IN_WESPIN_GAMES_LABEL} (${streamView.games.length})`}
									text={false}
									dataAt="collapse-button-games-list u-mb1"
									className="we-spin-buyin__collapse"
									icon={<Icon className="collapse__continue-icon" type={ICON_BOTTOM} />}
								>
									{streamView.games.map(({ gameName }) => (
										<Caption key={gameName}>{gameName}</Caption>
									))}
								</Collapse>
							</CollapseGroup>
						)}

						{ownsThisEvent && !PAST_STATES.includes(streamView.state) && (
							<CollapseGroup className="u-mt2 u-mb2">
								<Collapse
									label={translations.WE_SPIN_DASHBOARD_LIVE_EVENT_DETAIL_EDIT_EVENT}
									link={transformPath(
										`${ROUTE_ACCOUNT_WE_SPIN_DASHBOARD_EDIT_SESSION}/${params.streamRef}`
									)}
									type="link"
									returnLink={transformPath(`${ROUTE_WE_SPIN_GAME_ROOM_BUY_IN}/${params.streamRef}`)}
									dataAt="collapse-button-edit-event"
									className="we-spin-buyin__collapse"
									icon={<Icon className="collapse__continue-icon" type={ICON_RIGHT} />}
								/>
								<Collapse
									label={translations.WE_SPIN_DASHBOARD_SCHEDULED_REMOVE_STREAM_BUTTON}
									type="button"
									dataAt="collapse-button-remove-event"
									className="we-spin-buyin__collapse"
									icon={<Icon className="collapse__continue-icon" type={ICON_TRASH_LINE} />}
									onClick={() => {
										setConfirmRemove(true);
									}}
								/>
							</CollapseGroup>
						)}
						<Collapse
							label={translations.WE_SPIN_BUY_IN_WESPIN_TOC_LABEL}
							link={transformPath(`${ROUTE_WE_SPIN_GAME_ROOM_TOC}/${params.streamRef}`)}
							type="link"
							dataAt="collapse-button-games-excluded-bonus"
							className="we-spin-game-room__collapse"
							icon={<Icon className="collapse__continue-icon" type={ICON_RIGHT} />}
						/>
					</div>
				</div>
			</SidebarContent>
			{!PAST_STATES.includes(streamView.state) && (
				<SidebarFooter>
					{streamView.state !== PENDING_START &&
						streamView.state !== RUNNING &&
						streamView.joined &&
						!isFreeWeSpin && (
							<BoxWithDisabledBackgroundColor
								flex="1"
								mb="0"
								p="5"
								display="flex"
								flexDirection="column"
								alignItems="center"
								className="wespin-buyin-button-large we-spin-buy-in-footer"
							>
								<ButtonPrimary
									onClick={() => {
										setAdjustAmountQuestion(true);
									}}
									disabled={false}
									className="u-mb1"
								>
									{translations.WE_SPIN_PREVIEW_ROOM_EDIT_AMOUNT}
								</ButtonPrimary>
								<RefundButton
									onClick={() => {
										setShowRefundConfirmation(true);
									}}
									disabled={false}
									className="refund-button"
								>
									<LabelRegular>{translations.WE_SPIN_PREVIEW_ROOM_REFUND_BUTTON}</LabelRegular>
								</RefundButton>
							</BoxWithDisabledBackgroundColor>
						)}
					{!isFreeWeSpin &&
						!streamView.joined &&
						streamView.state !== PENDING_START &&
						streamView.state !== RUNNING && (
							<BoxWithBackgroundColor
								flex="1"
								mb="0"
								p="5"
								className="wespin-large-buyin-button-container"
							>
								<ButtonPrimary onClick={showUsernameQuestionOrBuyInQuestion} disabled={buyInDisabled}>
									{`${translations.WE_SPIN_BUY_IN_BUTTON} ${!buyInDisabled ? buyInBalance : ''}`}
								</ButtonPrimary>
							</BoxWithBackgroundColor>
						)}
					{isFreeWeSpin &&
						!streamView.joined &&
						streamView.state !== PENDING_START &&
						streamView.state !== ANNOUNCED &&
						streamView.state !== RUNNING && (
							<BoxWithDisabledBackgroundColor
								disabled={!hasMadeDeposit}
								flex="1"
								mb="0"
								p="5"
								display="flex"
								flexDirection="column"
								alignItems="center"
								className="wespin-buyin-button-large"
							>
								{!hasMadeDeposit && (
									<Caption pb="5">
										{translations.WE_SPIN_BUY_IN_FREE_WE_SPIN_DEPOSIT_REQUIRED}
									</Caption>
								)}
								<ButtonPrimary
									onClick={showUsernameQuestionOrJoinFreeWeSpin}
									disabled={!hasMadeDeposit || streamView.playerCapMet}
									whileTap={!hasMadeDeposit && undefined}
								>
									{translations.WE_SPIN_BUY_IN_FREE_WE_SPIN_REGISTER}
								</ButtonPrimary>
							</BoxWithDisabledBackgroundColor>
						)}
					{isFreeWeSpin &&
						streamView.joined &&
						streamView.state !== PENDING_START &&
						streamView.state !== RUNNING && (
							<BoxWithDisabledBackgroundColor
								flex="1"
								mb="0"
								p="5"
								display="flex"
								flexDirection="column"
								alignItems="center"
								className="wespin-buyin-button-large"
							>
								<ButtonPrimary
									onClick={() => {
										setShowUnregisterFreeWeSpin(true);
									}}
								>
									Unregister
								</ButtonPrimary>
							</BoxWithDisabledBackgroundColor>
						)}
				</SidebarFooter>
			)}
		</Sidebar>
	);
};

export default WeSpinGameRoomBuyIn;
